import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, Typography, Chip, Divider, GridLegacy as Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Button, CircularProgress, TablePagination,
} from '@mui/material';
import { employeeDetailService, attendanceService } from '../services/api';
import { usePagination } from '../hooks/usePagination';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const verificationColor = (status?: string) =>
  status === 'Approved' ? 'success.main' : status === 'Rejected' ? 'error.main' : 'warning.main';

interface EmployeeDetailDrilldownProps {
  /** Full user row (id, username, fullName, email, role, isActive, clientName, salary, ...) —
   * passed in by the caller rather than re-fetched, since both places this is used already have
   * it loaded. Dialog is open whenever this is non-null. */
  user: any | null;
  onClose: () => void;
  /** Called after a document is approved/rejected, so the caller can refresh its own list. */
  onDocumentVerified?: () => void;
}

// Full "everything about this employee" view — profile, identity documents (with verify
// actions), and attendance history. Deliberately works the same regardless of the employee's
// active/inactive status: none of the endpoints this calls filter by IsActive (see
// AttendanceController.GetUserAttendance's remarks) — deactivation should never make an
// employee's history inaccessible to admins reviewing it later. Shared between the Employees
// list (EmployeeManagement) and the Employee Details / document-verification page
// (EmployeeDetails), so both stay in sync instead of maintaining two "view employee" dialogs.
const EmployeeDetailDrilldown: React.FC<EmployeeDetailDrilldownProps> = ({ user, onClose, onDocumentVerified }) => {
  const [detail, setDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [attendance, setAttendance] = useState<any[] | null>(null);
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(today());

  const loadDetail = useCallback(async () => {
    if (!user) return;
    setLoadingDetail(true);
    try {
      const data = await employeeDetailService.getEmployeeDetail(user.id);
      setDetail(data?.id ? data : null);
    } catch {
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [user]);

  const loadAttendance = useCallback(async () => {
    if (!user) return;
    setAttendance(null);
    try {
      const data = await attendanceService.getUserAttendance(user.id, fromDate, toDate);
      setAttendance(data || []);
    } catch {
      setAttendance([]);
    }
  }, [user, fromDate, toDate]);

  useEffect(() => {
    if (user) {
      loadDetail();
      loadAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user) loadAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const handleVerify = async (documentType: string, status: string) => {
    if (!user) return;
    const reason = status === 'Rejected' ? window.prompt('Enter rejection reason:') : null;
    if (status === 'Rejected' && !reason) return;
    try {
      await employeeDetailService.verifyDocument({ userId: user.id, documentType, status, rejectionReason: reason });
      loadDetail();
      onDocumentVerified?.();
    } catch {
      // Non-fatal for the dialog itself — the caller's own page still shows its own error state.
    }
  };

  // Called unconditionally (rules of hooks) even before the `!user` early return / while
  // attendance is still loading — `attendance ?? []` just pages an empty list until then.
  const { pagedRows: pagedAttendance, paginationProps: attendancePaginationProps } = usePagination(attendance ?? []);

  if (!user) return null;

  const presentCount = attendance?.filter(a => a.status === 'Present').length ?? 0;
  const totalCount = attendance?.length ?? 0;

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {user.fullName}
          <Chip label={user.isActive ? 'Active' : 'Inactive'} color={user.isActive ? 'success' : 'default'} size="small" />
          <Chip label={user.role} size="small" variant="outlined" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {user.username} · {user.email} {user.clientName ? `· ${user.clientName}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loadingDetail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : !detail ? (
          <Typography color="text.secondary">No profile details have been added for this employee yet.</Typography>
        ) : (
          <>
            <Typography variant="subtitle2" gutterBottom>Profile</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[
                ['Phone', detail.phoneNumber], ['Date of Birth', detail.dateOfBirth ? new Date(detail.dateOfBirth).toLocaleDateString() : '-'],
                ['Joining Date', detail.joiningDate ? new Date(detail.joiningDate).toLocaleDateString() : '-'], ['Department', detail.department],
                ['Designation', detail.designation], ['Address', detail.address],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label as string}>
                  <Typography variant="body2"><strong>{label}:</strong> {value || '-'}</Typography>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Identity Documents</Typography>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>PAN:</strong> {detail.panNumber || '-'}</Typography>
                <Typography variant="caption" sx={{ color: verificationColor(detail.panVerificationStatus) }}>
                  {detail.panVerificationStatus || 'Pending'}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {detail.panCardUrl && <Button size="small" onClick={() => window.open(detail.panCardUrl, '_blank')}>View</Button>}
                  {detail.panVerificationStatus !== 'Approved' && (
                    <Button size="small" color="success" onClick={() => handleVerify('PAN', 'Approved')}>Approve</Button>
                  )}
                  {detail.panVerificationStatus !== 'Rejected' && (
                    <Button size="small" color="error" onClick={() => handleVerify('PAN', 'Rejected')}>Reject</Button>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2"><strong>Aadhar:</strong> {detail.aadharNumber || '-'}</Typography>
                <Typography variant="caption" sx={{ color: verificationColor(detail.aadharVerificationStatus) }}>
                  {detail.aadharVerificationStatus || 'Pending'}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {detail.aadharCardUrl && <Button size="small" onClick={() => window.open(detail.aadharCardUrl, '_blank')}>View</Button>}
                  {detail.aadharVerificationStatus !== 'Approved' && (
                    <Button size="small" color="success" onClick={() => handleVerify('Aadhar', 'Approved')}>Approve</Button>
                  )}
                  {detail.aadharVerificationStatus !== 'Rejected' && (
                    <Button size="small" color="error" onClick={() => handleVerify('Aadhar', 'Rejected')}>Reject</Button>
                  )}
                </Box>
              </Grid>
            </Grid>
            {detail.resumeUrl && <Button size="small" onClick={() => window.open(detail.resumeUrl, '_blank')}>View Resume</Button>}

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2"><strong>Bank:</strong> {detail.bankName || '-'} ({detail.bankAccountNumber || '-'}) · IFSC {detail.bankIFSC || '-'}</Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          <Typography variant="subtitle2">
            Attendance History {totalCount > 0 && `— ${presentCount}/${totalCount} Present in range`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="From" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ max: toDate }} />
            <TextField size="small" label="To" type="date" value={toDate} onChange={e => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ max: today(), min: fromDate }} />
          </Box>
        </Box>

        {attendance === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Date', 'Check In', 'Check Out', 'Status', 'Remarks'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendance.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No attendance records in this date range.</TableCell></TableRow>
                  ) : pagedAttendance.map((a: any) => (
                    <TableRow key={a.id}>
                      <TableCell>{new Date(a.date).toLocaleDateString()}</TableCell>
                      <TableCell>{a.checkInTime || '-'}</TableCell>
                      <TableCell>{a.checkOutTime || '-'}</TableCell>
                      <TableCell>
                        <Chip label={a.status} size="small" color={a.status === 'Present' ? 'success' : a.status === 'Half Day' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>{a.remarks || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {attendance.length > 0 && <TablePagination {...attendancePaginationProps} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeDetailDrilldown;
